import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';

export const useNavigation = () => {
  const navigate = useNavigate();
  const { sessionData, eid } = useSession();

  // Helper function to check if challenge is done
  const isChallengeDone = (status) => {
    return status === 'cs_done' || status === 'cs_submitted' || status === 'cs_assessing';
  };

  // Step definitions with constraint logic
  const stepDefinitions = [
    {
      key: 'welcome',
      path: '/',
      order: 0,
      constraints: () => true // Always available
    },
    {
      key: 'resume',
      path: '/resume',
      order: 1,
      constraints: (data) => {
        if (!data?.steps?.step_upload_resume) return false;
        return data.questionnaire_status === 'qs_not_started' && 
               data.challenge_status === 'cs_not_started';
      }
    },
    {
      key: 'videointro',
      path: '/videointro',
      order: 2,
      constraints: (data) => {
        if (!data?.steps?.step_video_intro) return false;
        return data.resumeReceived === true &&
               data.questionnaire_status === 'qs_not_started' && 
               data.challenge_status === 'cs_not_started';
      }
    },
    {
      key: 'notice',
      path: '/notice',
      order: 3,
      constraints: (data) => {
        // Resume must be uploaded IF step exists
        if (data.steps.step_upload_resume && !data.resumeReceived) return false;
        
        // Video must be uploaded IF step exists
        if (data.steps.step_video_intro && !data.intro_video_received) return false;
        
        return data.questionnaire_status === 'qs_not_started' && 
               data.challenge_status === 'cs_not_started';
      }
    },
    {
      key: 'questionnaire',
      path: '/questionnaire',
      order: 4,
      constraints: (data) => {
        if (!data?.steps?.step_questionnaire) return false;
        
        // If questionnaire is already started, allow access regardless of prerequisites
        if (data.questionnaire_status === 'qs_started') return true;
        
        // Resume must be uploaded IF step exists
        if (data.steps.step_upload_resume && !data.resumeReceived) return false;
        
        // Video must be uploaded IF step exists
        if (data.steps.step_video_intro && !data.intro_video_received) return false;
        
        return data.challenge_status === 'cs_not_started';
      }
    },
    {
      key: 'challenge',
      path: '/challenge',
      order: 5,
      constraints: (data) => {
        // If challenge is already started, allow access regardless of prerequisites
        if (data.challenge_status === 'cs_started') return true;
        
        // QUESTIONNAIRE SKIPPING: If questionnaire step is disabled, allow challenge access immediately
        if (!data?.steps?.step_questionnaire) return true;
        
        return data.questionnaire_status === 'qs_done';
      }
    },
    {
      key: 'finished',
      path: '/finished',
      order: 6,
      constraints: (data) => {
        // QUESTIONNAIRE SKIPPING: Check questionnaire status only if the questionnaire step is enabled
        const questionnaireComplete = !data?.steps?.step_questionnaire || 
                                     data.questionnaire_status === 'qs_done';
        
        return questionnaireComplete && isChallengeDone(data.challenge_status);
      }
    }
  ];

  // Get accessible steps
  const accessibleSteps = useMemo(() => {
    if (!sessionData) return [stepDefinitions[0]]; // Only welcome if no session
    return stepDefinitions.filter(step => step.constraints(sessionData));
  }, [sessionData]);

  // Get current step from pathname
  const getCurrentStep = (pathname) => {
    if (pathname === '/' || pathname.match(/^\/[^/]+$/)) {
      return stepDefinitions[0]; // welcome
    }
    return stepDefinitions.find(step => 
      pathname.includes(step.path) && step.path !== '/'
    );
  };

  // Find next accessible step
  const getNextAccessibleStep = (currentStep) => {
    if (!currentStep) return null;
    const nextSteps = accessibleSteps.filter(step => step.order > currentStep.order);
    return nextSteps.length > 0 ? nextSteps[0] : null;
  };

  // Find previous accessible step
  const getPreviousAccessibleStep = (currentStep) => {
    if (!currentStep) return null;
    const prevSteps = accessibleSteps.filter(step => step.order < currentStep.order);
    return prevSteps.length > 0 ? prevSteps[prevSteps.length - 1] : null;
  };

  // Navigation functions
  const navigateToStep = (stepKey) => {
    const step = accessibleSteps.find(s => s.key === stepKey);
    if (!step) return false;
    
    const path = step.path === '/' ? `/${eid}` : `${step.path}/${eid}`;
    navigate(path);
    
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    
    return true;
  };

  const navigateNext = (currentPath) => {
    const currentStep = getCurrentStep(currentPath);
    const nextStep = getNextAccessibleStep(currentStep);
    
    if (nextStep) {
      return navigateToStep(nextStep.key);
    }
    return false;
  };

  const navigatePrevious = (currentPath) => {
    const currentStep = getCurrentStep(currentPath);
    const prevStep = getPreviousAccessibleStep(currentStep);
    
    if (prevStep) {
      return navigateToStep(prevStep.key);
    }
    return false;
  };

  // Check if step is accessible
  const isStepAccessible = (stepKey) => {
    return accessibleSteps.some(step => step.key === stepKey);
  };

  // Get most appropriate step for redirects
  const getAppropriateStep = () => {
    // Simply return the first accessible step after welcome
    for (const step of accessibleSteps) {
      if (step.key === 'welcome') continue; // Skip welcome
      return step; // Return any step including finished
    }
    
    return accessibleSteps[0]; // Fallback to welcome
  };

  return {
    accessibleSteps,
    getCurrentStep,
    navigateNext,
    navigatePrevious,
    isStepAccessible,
    getAppropriateStep,
    isChallengeDone
  };
};
